import React, { useCallback } from 'react';
import AddIcon from '@atlaskit/icon/core/add';
import SettingsIcon from '@atlaskit/icon/core/settings';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import LinkItem from '@atlaskit/menu/link-item';
import ButtonItem from '@atlaskit/menu/button-item';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { FIXED_ITEM_BUTTON } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_TWO } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_PLANS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { useCreateSamplePlan } from '@jira/portfolio-3__create-sample-plan/src/controllers/index.tsx';
import { preloadCreateSamplePlanContent } from '@jira/portfolio-3__create-sample-plan/src/ui/content/async.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';

const adminSettings = defineMessage({
	defaultMessage: 'Admin configuration',
	id: 'navigation-apps-sidebar-nav4-plans.plans.common.action-buttons.more-menu-button.admin-settings',
	description:
		'Used as the button tooltip for the admin configuration cog button. Item navigates to the Plans settings page',
});

const plansMoreMenu = defineMessage({
	defaultMessage: 'Plans',
	id: 'navigation-apps-sidebar-nav4-plans.plans.common.action-buttons.more-menu-button.plans-more-menu',
	description: 'Used as label for the plans more menu button',
});

const createDemoPlan = defineMessage({
	defaultMessage: 'Create demo plan',
	id: 'navigation-apps-sidebar-nav4-plans.plans.common.action-buttons.more-menu-button.create-demo-plan',
	description: 'Used as the menu-title for the Create demo plan menu link item',
});

type MoreMenuItemsProps = MoreNavMenuButtonMenuItemsProps & {
	hasCreateSamplePlanPermissions: boolean;
	hasGlobalPlansAdminPermissions: boolean;
};

const MoreMenuItems = ({
	hasCreateSamplePlanPermissions,
	hasGlobalPlansAdminPermissions,
	onClose,
}: MoreMenuItemsProps) => {
	const { formatMessage } = useIntl();
	const [{ hasBeenInitialised }, { open: openCreateSamplePlanModal }] = useCreateSamplePlan();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const createDemoPlanClickHandler = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'dropdownItem',
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_PLANS],
				attributes: {
					level: LEVEL_TWO,
					componentType: FIXED_ITEM_BUTTON,
					itemId: 'createDemoPlan',
				},
			});
			// Close the dropdown popup before opening the modal so the popup's
			// focus trap is torn down first; otherwise the modal's FocusLock
			// cannot move focus into the dialog (A11Y-29104, WCAG 2.4.3).
			if (expVal('a11y-29104_demo_plan_modal_autofocus_fix', 'isEnabled', false)) {
				onClose?.(event);
			}
			openCreateSamplePlanModal();
		},
		[onClose, openCreateSamplePlanModal, createAnalyticsEvent],
	);

	const adminSettingsClickHandler = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_PLANS],
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_BUTTON,
				itemId: 'adminSettings',
			},
		});
	}, [createAnalyticsEvent]);

	return (
		<MenuGroup>
			{(hasCreateSamplePlanPermissions || hasGlobalPlansAdminPermissions) && (
				<Section isList>
					{hasCreateSamplePlanPermissions && (
						<ButtonItem
							iconBefore={<AddIcon label="" spacing="spacious" color={token('color.icon')} />}
							onClick={createDemoPlanClickHandler}
							isDisabled={!hasBeenInitialised}
						>
							{formatMessage(createDemoPlan)}
						</ButtonItem>
					)}
					{hasGlobalPlansAdminPermissions && (
						<LinkItem
							iconBefore={<SettingsIcon label="" spacing="spacious" color={token('color.icon')} />}
							href="/jira/plans/settings/permissions"
							onClick={adminSettingsClickHandler}
						>
							{formatMessage(adminSettings)}
						</LinkItem>
					)}
				</Section>
			)}
			{!fg('navx-global-side-nav') && <HideL1MenuItemSection menuId={L1_MENU_ID.PLANS} />}
		</MenuGroup>
	);
};

export const MoreMenuButton = ({
	hasCreateSamplePlanPermissions,
	hasGlobalPlansAdminPermissions,
}: {
	hasCreateSamplePlanPermissions: boolean;
	hasGlobalPlansAdminPermissions: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const clickHandler = useCallback(() => {
		if (fg('preload_plan_sample_create_modal_on_menu_open')) {
			// Warm the modal chunk while the menu is open.
			preloadCreateSamplePlanContent();
		}
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_PLANS],
			attributes: {
				level: '1',
				componentType: 'fixedItemButton',
				itemId: 'meatballDropdown',
			},
		});
	}, [createAnalyticsEvent]);

	const MenuItems = useCallback(
		({ onClose }: MoreNavMenuButtonMenuItemsProps) => (
			<MoreMenuItems
				hasCreateSamplePlanPermissions={hasCreateSamplePlanPermissions}
				hasGlobalPlansAdminPermissions={hasGlobalPlansAdminPermissions}
				onClose={onClose}
			/>
		),
		[hasCreateSamplePlanPermissions, hasGlobalPlansAdminPermissions],
	);

	if (
		!hasCreateSamplePlanPermissions &&
		!hasGlobalPlansAdminPermissions &&
		fg('navx-global-side-nav')
	) {
		return <></>;
	}

	return (
		<MoreNavMenuButton
			MenuItems={MenuItems}
			name={formatMessage(plansMoreMenu)}
			onOpen={clickHandler}
		/>
	);
};
